import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import Layout from '../components/Layout';
import PasswordChangeModal from '../components/PasswordChangeModal';
import OrganisationSettings from '../components/OrganisationSettings';
import SetupWizard from '../components/SetupWizard';
import IntegrationList from '../components/IntegrationList';
import IntegrationConfig from '../components/IntegrationConfig';
import IntegrationLogs from '../components/IntegrationLogs';
import TargetCompanies from '../components/TargetCompanies';
import ScoringConfig from '../components/ScoringConfig';
import PipelineRuns from '../components/PipelineRuns';

export default function AdminConsole() {
  const [user, setUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [organisation, setOrganisation] = useState(null);
  const [organisationStatus, setOrganisationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'settings', 'users', 'integrations', 'target-companies', 'scoring', 'pipeline-runs'
  const [showWizard, setShowWizard] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrationView, setIntegrationView] = useState('list'); // 'list' or 'config' or 'logs'

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const changePasswordParam = urlParams.get('changePassword');
    
    // First, try to get user from sessionStorage (from login/setup-password redirect)
    const userStr = sessionStorage.getItem('signalcue_user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        // Validate it's an admin user before setting
        if (userData.role === 'admin') {
          setUser(userData);
          setLoading(false);
          
          if (changePasswordParam === 'true' && userData.must_change_password) {
            setShowPasswordModal(true);
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }
        }
      } catch (e) {
        console.error('Failed to parse user from sessionStorage:', e);
      }
    }
    
    // Always refresh user data from API to ensure it's up to date
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && !user.must_change_password) {
      loadOrganisationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    // Check if wizard should be shown
    if (organisationStatus && organisationStatus.setup_status !== 'ready') {
      // Check if this is first login (setup_status is not_configured or partially_configured)
      // and wizard hasn't been started
      if (organisationStatus.setup_status === 'not_configured' || 
          (organisationStatus.setup_status === 'partially_configured' && organisationStatus.pending_steps.length > 0)) {
        setShowWizard(true);
      }
    }
  }, [organisationStatus]);

  const loadOrganisationData = async () => {
    try {
      setLoading(true);
      setError('');
      const [orgData, statusData] = await Promise.all([
        authService.getOrganisation(),
        authService.getOrganisationStatus()
      ]);
      setOrganisation(orgData);
      setOrganisationStatus(statusData);
    } catch (err) {
      console.error('Failed to load organisation data:', err);
      setError(err.message || 'Failed to load organisation data');
    } finally {
      setLoading(false);
    }
  };

  const handleOrganisationUpdate = (updatedOrg) => {
    setOrganisation(updatedOrg);
    // Reload status to update progress
    loadOrganisationData();
  };

  const loadUserData = async () => {
    try {
      setLoading(true);
      const userData = await authService.getCurrentUser();
      if (!userData) {
        const loginUrl = process.env.REACT_APP_LOGIN_URL || (window.location.hostname.includes('-staging') ? window.location.origin : window.location.origin.replace('app.signalcue.ai', 'signalcue.ai'));
        window.location.href = `${loginUrl}/login`;
        return;
      }
      
      // Check if user has admin role
      if (userData.role !== 'admin') {
        // Redirect non-admin users to candidates page
        window.location.href = '/candidates';
        return;
      }
      
      setUser(userData);
      setLoading(false);
      
      // Update sessionStorage with latest user data
      sessionStorage.setItem('signalcue_user', JSON.stringify(userData));
      
      if (userData.must_change_password) {
        setShowPasswordModal(true);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      setLoading(false);
      setError('Failed to load user data. Please try logging in again.');
      // Don't redirect immediately - let user see the error
      // They can manually navigate to login if needed
    }
  };

  const handlePasswordChanged = async () => {
    const updatedUserData = await authService.getCurrentUser();
    if (updatedUserData) {
      setUser(updatedUserData);
    }
  };

  const handleWizardComplete = async () => {
    setShowWizard(false);
    // Reload organisation data to reflect completed status
    await loadOrganisationData();
  };

  const handleWizardCancel = () => {
    if (window.confirm('Are you sure you want to cancel the setup wizard? You can resume it later.')) {
      setShowWizard(false);
    }
  };

  // Show loading state while fetching user data
  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#00C6AE] mb-4"></div>
          <p className="text-gray-600">Loading admin console...</p>
        </div>
      </div>
    );
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'partially_configured':
        return 'bg-yellow-100 text-yellow-800';
      case 'not_configured':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'partially_configured':
        return 'Partially Configured';
      case 'not_configured':
      default:
        return 'Not Configured';
    }
  };

  return (
    <Layout user={user}>
      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordChangeModal
          user={user}
          onPasswordChanged={handlePasswordChanged}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      {/* Setup Wizard */}
      {showWizard ? (
        <SetupWizard
          onComplete={handleWizardComplete}
          onCancel={handleWizardCancel}
        />
      ) : (
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-[#00C6AE] text-[#00C6AE]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'settings'
                    ? 'border-[#00C6AE] text-[#00C6AE]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Organisation Settings
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'users'
                    ? 'border-[#00C6AE] text-[#00C6AE]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => {
                  setActiveTab('integrations');
                  setIntegrationView('list');
                  setSelectedIntegration(null);
                }}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'integrations'
                    ? 'border-[#00C6AE] text-[#00C6AE]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Integrations
              </button>
              <button
                onClick={() => setActiveTab('target-companies')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'target-companies'
                    ? 'border-[#00C6AE] text-[#00C6AE]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Target Companies
              </button>
              <button
                onClick={() => setActiveTab('scoring')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'scoring'
                    ? 'border-[#00C6AE] text-[#00C6AE]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Candidate scoring
              </button>
              <button
                onClick={() => setActiveTab('pipeline-runs')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'pipeline-runs'
                    ? 'border-[#00C6AE] text-[#00C6AE]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pipeline runs
              </button>
            </nav>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Organisation Info Card */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Organisation Overview</h2>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
                  <p className="mt-2 text-gray-500">Loading organisation data...</p>
                </div>
              ) : organisation ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Organisation Name</label>
                    <p className="text-lg font-semibold text-gray-900">{organisation.name}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
                    <p className="text-lg text-gray-900">{organisation.country || 'Not specified'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Timezone</label>
                    <p className="text-lg text-gray-900">{organisation.timezone || 'UTC'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Data Residency Region</label>
                    <p className="text-lg text-gray-900">{organisation.data_residency_region || 'APAC'}</p>
                  </div>
                  
                  {organisation.website && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Website</label>
                      <a 
                        href={organisation.website.startsWith('http') ? organisation.website : `https://${organisation.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lg text-[#00C6AE] hover:underline"
                      >
                        {organisation.website}
                      </a>
                    </div>
                  )}
                  
                  {organisation.agency_size && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Agency Size</label>
                      <p className="text-lg text-gray-900">{organisation.agency_size} users</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No organisation data available</p>
              )}
            </div>

            {/* Setup Status Card */}
            {organisationStatus && (
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-gray-900">Setup Status</h2>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(organisationStatus.setup_status)}`}>
                    {getStatusLabel(organisationStatus.setup_status)}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Onboarding Progress</span>
                    <span className="text-sm font-semibold text-gray-900">{organisationStatus.progress_percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-[#00C6AE] h-3 rounded-full transition-all duration-300"
                      style={{ width: `${organisationStatus.progress_percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Completed Steps */}
                {organisationStatus.completed_steps.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Completed Steps</h3>
                    <ul className="space-y-2">
                      {organisationStatus.completed_steps.map((step, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-700">
                          <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Pending Steps */}
                {organisationStatus.pending_steps.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Next Steps</h3>
                    <ul className="space-y-2">
                      {organisationStatus.pending_steps.map((step, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-700">
                          <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Start Setup Wizard Button (for Step 4) */}
                {organisationStatus.setup_status !== 'ready' && !showWizard && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowWizard(true)}
                      className="px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] transition-colors"
                    >
                      Start First-Time Setup Wizard
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <OrganisationSettings 
            organisation={organisation} 
            onUpdate={handleOrganisationUpdate}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Users</h2>
            {organisationStatus && organisationStatus.setup_status === 'ready' ? (
              <div>
                <p className="text-gray-600 mb-4">
                  User management is now available. You can view and manage all team members.
                </p>
                <button
                  onClick={() => setShowWizard(true)}
                  className="px-4 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] transition-colors"
                >
                  Manage Users
                </button>
              </div>
            ) : (
              <div>
                <p className="text-gray-600 mb-4">
                  Complete the first-time setup wizard to enable full user management.
                </p>
                <button
                  onClick={() => setShowWizard(true)}
                  className="px-4 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] transition-colors"
                >
                  Start Setup Wizard
                </button>
              </div>
            )}
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <>
            {integrationView === 'list' && (
              <IntegrationList
                onConfigure={(type, view) => {
                  setSelectedIntegration(type);
                  setIntegrationView(view || 'config');
                }}
              />
            )}
            {integrationView === 'config' && selectedIntegration && (
              <IntegrationConfig
                integrationType={selectedIntegration}
                onCancel={() => {
                  setSelectedIntegration(null);
                  setIntegrationView('list');
                }}
                onSave={() => {
                  setSelectedIntegration(null);
                  setIntegrationView('list');
                }}
              />
            )}
            {integrationView === 'logs' && selectedIntegration && (
              <IntegrationLogs
                integrationType={selectedIntegration}
                onCancel={() => {
                  setSelectedIntegration(null);
                  setIntegrationView('list');
                }}
              />
            )}
          </>
        )}

        {/* Target Companies Tab */}
        {activeTab === 'target-companies' && (
          <TargetCompanies />
        )}

        {/* Candidate scoring Tab */}
        {activeTab === 'scoring' && (
          <ScoringConfig />
        )}

        {/* Pipeline runs Tab (admin-only: Step 1/2/3 visibility) */}
        {activeTab === 'pipeline-runs' && (
          <PipelineRuns />
        )}
      </div>
      )}
    </Layout>
  );
}
